import { IRentalCalendarProps } from "./RentalCalendar";
import { IListing, IListingSummary, IMediaItem, IPerson } from "../../lib";
import { ILinkFunc } from "../../lib/util";
import { ICarouselProps } from "../Carousel";
import { IContactFormProps } from "../Forms";
import { IListingGalleryProps } from "../Gallery";
import { IListingCardProps } from "./ListingCard";
import { IListingContactProps } from "./ListingContact";
import { IListingDetailsProps } from "./ListingDetails";
import { IListingMapProps } from "./ListingMap";
import { IListingSummaryProps } from "./ListingSummary";
import { IListingVitalsProps } from "./ListingVitals";
import { IRentalVitalsProps } from "./RentalVitals";
import { IListingDocumentsProps } from "./ListingDocuments";
import { IListingShareProps } from "./ListingShare";
import { IBackgoundImageProps } from "../Media/BackgroundImage";
import { IListingOpenHouseProps } from "./ListingOpenHouse";
import { IListingDisclaimer } from "./IListingDisclaimer";

/**
 * Common interface for all variants of ListingLayout... components.
 */
export interface IListingLayoutProps {
  /**
   * The listing to display in this layout.
   */
  listing: IListing;
  /**
   * Optional title to include as an h1 tag within the layout.
   */
  title?: string;
  /**
   * Linking function component.
   */
  linkFunc?: ILinkFunc;
  /**
   * One or more agent contacts to show in the contact area.
   */
  agents?: IPerson[];
  /**
   * One or more similar listings to display in carousel.
   */
  similar?: IListingSummary[];
  /**
   * Properties to override in similar carousel.
   */
  similarCarouselProps?: Partial<ICarouselProps>;
  /**
   * Properties to override in similar cards within the carousel.
   */
  similarCardProps?: Partial<IListingCardProps>;
  /**
   * Properties to override in the gallery component.
   */
  galleryProps?: Partial<IListingGalleryProps>;
  /**
   * Properties to override in the carousel component.
   */
  carouselProps?: Partial<ICarouselProps>;
  /**
   * Properties to override in the details component.
   */
  detailsProps?: Partial<IListingDetailsProps>;
  /**
   * Properties to override in the rental calendar component.
   */
  calendarProps?: Partial<IRentalCalendarProps>;
  /**
   * Properties to override in the listing map component.
   */
  mapProps?: Partial<IListingMapProps>;
  /**
   * Properties to override in the listing vitals component.
   */
  vitalsProps?: Partial<IListingVitalsProps>;
  /**
   * Properties to override in the listing vitals component.
   */
  rentalVitalsProps?: Partial<IRentalVitalsProps>;
  /**
   * Properties to override in the listing contact component.
   */
  contactProps?: Partial<IListingContactProps>;
  /**
   * Properties to override in the listing summary component.
   */
  summaryProps?: Partial<IListingSummaryProps>;
  /**
   * Properties to override in the contact form component.
   */
  formProps?: Partial<IContactFormProps>;
  showForm?: boolean;
  /**
   * Class name to apply to any "content rows" in the layout, eg for max-width
   */
  rowClassName?: string;
  /**
   * Class name to apply to the form wrapping column in the layout
   */
  formColumnClassName?: string;
  showContactUsForm?: boolean;
  contactUsFormProps?: Partial<IContactFormProps>;
  className?: string;
  /**
   * Properties to override in the documents component.
   */
  documentsProps?: Partial<IListingDocumentsProps>;
  displayMediaSection?: boolean;
  mediaSectionTitle?: string;
  photosTabTitle?: string;
  propertyVideoTabTitle?: string;
  virtualTourTabTitle?: string;
  floorplansTabTitle?: string;
  displayDisclaimer?: boolean;
  disclaimers?: IListingDisclaimer[];
  disclaimerCssClasses?: string;
  /**
   * Properties to override in the gallery carousel component.
   */
  galleryCarouselProps?: Partial<ICarouselProps>;
  listingShareProps?: Partial<IListingShareProps>;
  subtitle?: string;
  headingProps?: Partial<IBackgoundImageProps>;
  /**
  * Properties to override in the listing open house component.
  */
  openHouseProps?: Partial<IListingOpenHouseProps>;
  showDivider?: boolean;
  dividerClassName?: string;
  heroWrapperClassName?: string;
  mediaSectionLayout?: EMediaSectionLayout;
  listingFormLocation?: ELisitngFormLocation;

  showAllRates?: boolean;
  /**
   * 
   */
  showFlags?: boolean;

  showPrint?: boolean;

  officeLogo?: IMediaItem;

  disclaimerLocation?: EDisclaimerLocation;

  optionalFormChildrenProp?: (listing: IListing) => React.ReactNode;

  displayCourtesyUnderGallery?: boolean;
  showRatesAndAvailabilityBlade?: boolean;
  showRentalVideo?: boolean;
}

export enum EMediaSectionLayout {
  tabs = "tabs",
  separated = "separated",
}

export enum ELisitngFormLocation {
  summarySide = "summarySide",
  pageSection = "pageSection"
}

export const RateTypes = {
  "day": "Dayly",
  "week": "Weekly",
  "month": "Monthly",
  "night": "Nightly",
  "yearly": "Year",
  "year": "Year",
  "summer": "Summer",
  "winter": "Winter",
  "monthSeasonal": "Seasonal Monthly",
  "longTerm": "Long Term",
  "holiday": "Holiday Nightly",
}

export enum EDisclaimerLocation {
  bottom = "bottom", //last element
  afterDetails = "afterDetails",
}